// IM-EXT-005 — jornada do enriquecimento empresarial: progresso real por etapa, resumo, cobertura por seção, revisão por
// seção com filtros e ações em lote, histórico de execuções e fontes consultadas. Todo conteúdo externo é texto (escapado).
import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';
import type { AnalysisRunDto, CompanyContextDto, ContextActivityDto, ContextEntityKind, ContextEvidenceDto, ContextSourceDto, FindingDto } from '@intelimove/contracts';
import { AsyncView } from '../../components/States';
import { useToast } from '../../components/Toast';
import { relativeTime } from '../../data/adapters';
import {
  contextAdapter, COVERAGE_TXT, FIELD_LABEL, IDENTITY_TXT, MERGE_TXT, METHOD_TXT, NATURE_TONE, NATURE_TXT, ORIGIN_TXT, PIPELINE_STAGES, SECTION_SLUG, SECTION_TXT,
  SOURCE_TYPE_TXT, STAGE_LABEL, type ResearchTrigger,
} from '../../data/context';
import { newId } from '../../lib/id';
import { useResource } from '../../hooks/useResource';

type Ctx = CompanyContextDto;
const safeHref = (u: string | null | undefined) => (u && /^https?:\/\//i.test(u) ? u : undefined);
const show = (v: unknown): string => (v === null || v === undefined ? '—' : Array.isArray(v) ? v.join(', ') : typeof v === 'object' ? JSON.stringify(v) : String(v));
const active = (r: AnalysisRunDto | null) => !!r && (r.status === 'QUEUED' || r.status === 'RUNNING');

export function useAct() {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const run = async (fn: () => Promise<unknown>, ok: string | ((r: any) => string), after?: () => void) => {
    setBusy(true);
    try { const r = await fn(); toast(typeof ok === 'function' ? ok(r) : ok); after?.(); } catch (e) { toast((e as Error).message, 'error'); } finally { setBusy(false); }
  };
  return { busy, run };
}

export function Evidences({ items }: { items: Array<ContextEvidenceDto | null> }) {
  const list = items.filter((x): x is ContextEvidenceDto => !!x);
  if (!list.length) return null;
  return (
    <details className="evidence">
      <summary>Ver {list.length > 1 ? `${list.length} evidências` : 'evidência'}</summary>
      {list.map((e) => (
        <div key={e.id}>
          <blockquote>{e.excerpt}</blockquote>
          <span className="cell-sub">{safeHref(e.url) ? <a href={safeHref(e.url)} target="_blank" rel="noopener noreferrer">{e.title}</a> : e.title} · coletada {e.collectedAt ? relativeTime(e.collectedAt) : '—'}</span>
        </div>
      ))}
    </details>
  );
}

const PENDING_SECTIONS = ['identity', 'offerings', 'markets', 'icps', 'personas', 'differentiators', 'competitors', 'goals'] as const;
const clock = (iso: string | null) => (iso ? new Date(iso).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' }) : '—');
export const pendingTotal = (ctx: Ctx) => Object.values(ctx.pendingBySection ?? {}).reduce((a, b) => a + b, 0);
/** O que a execução parcial não concluiu (motivos gravados pelo pipeline). */
const partialReasons = (run: AnalysisRunDto) => run.notes.find((n) => n.startsWith('Execução parcial:'))?.replace('Execução parcial:', '').replace(/\.$/, '').split(';').map((x) => x.trim()).filter(Boolean) ?? [];
export const RERUN_NOTICE = 'O InteliMove pesquisará novamente o site e as fontes públicas. Informações já confirmadas não serão substituídas automaticamente. Novos dados e divergências serão apresentados para sua revisão.';

/**
 * IM-EXT-006 — ações da pesquisa da empresa, compartilhadas pela Visão geral e pela Identidade (mesmo serviço e estado).
 * Uma chave de idempotência por intenção e trava síncrona: duplo clique não cria duas execuções (o servidor também recusa
 * uma segunda execução ativa para o tenant).
 */
export function useResearch(reload: () => void) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  const inflight = useRef(false);
  const start = async (opts: { website?: string; saveWebsite?: boolean; force?: boolean; trigger: ResearchTrigger }) => {
    if (inflight.current) return false;
    inflight.current = true;
    setBusy(true);
    try {
      const run = await contextAdapter.analyze({ ...opts, key: `ui-${newId()}` });
      toast(opts.saveWebsite ? `Site salvo como ${run.domain}. Pesquisa iniciada — você pode continuar navegando.` : 'Pesquisa iniciada — você pode continuar navegando.');
      reload();
      return true;
    } catch (e) {
      toast((e as Error).message, 'error');
      reload();
      return false;
    } finally {
      inflight.current = false;
      setBusy(false);
    }
  };
  return { busy, start };
}

/** Validação leve no navegador; a normalização e a recusa de endereços internos (SSRF) são do servidor. */
const looksLikeSite = (v: string) => /^(https?:\/\/)?([a-z0-9-]+\.)+[a-z]{2,}(:\d+)?(\/.*)?$/i.test(v.trim());

function SiteForm({ initial, busy, onSubmit, onCancel, autoFocus, changing, locked }: {
  initial?: string | null; busy: boolean; onSubmit: (site: string) => void; onCancel?: () => void; autoFocus?: boolean; changing?: boolean; locked?: boolean;
}) {
  const [error, setError] = useState<string | null>(null);
  const submit = (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const v = String(new FormData(e.currentTarget).get('site') ?? '').trim();
    if (!looksLikeSite(v)) { setError('Informe um endereço válido, por exemplo suaempresa.com.br.'); return; }
    setError(null);
    onSubmit(v);
  };
  return (
    <form className="site-form" onSubmit={submit} noValidate>
      {changing && <p className="notice-inline">Uma nova pesquisa será feita para o novo site. Informações já confirmadas não são apagadas; o que divergir vai para a sua revisão. As sugestões ainda pendentes do site anterior saem da revisão e ficam no histórico.{locked ? ' Como a identidade já foi confirmada, só domínios já cadastrados da empresa são aceitos aqui — para trocar o domínio principal, use "Alterar dados cadastrais" em Identidade.' : ''}</p>}
      <label htmlFor="research-site">Site da empresa</label>
      <div className="inline-form">
        <input id="research-site" name="site" defaultValue={initial ?? ''} placeholder="ex.: suaempresa.com.br" autoComplete="url" inputMode="url" required autoFocus={autoFocus} aria-invalid={!!error} aria-describedby="research-site-hint" />
        <button className="cta" disabled={busy}>Salvar e pesquisar empresa</button>
        {onCancel && <button type="button" className="secondary" onClick={onCancel}>Cancelar</button>}
      </div>
      <span id="research-site-hint" className="cell-sub">Com ou sem https:// — ex.: suaempresa.com.br ou https://www.suaempresa.com.br</span>
      {error && <div className="form-error" role="alert">{error}</div>}
    </form>
  );
}

/**
 * IM-EXT-006 — card "Pesquisa da empresa": sempre oferece a próxima ação.
 * Estados: sem site (A), site sem pesquisa (B), em andamento (C), concluída (D), parcial (E), falha (F), troca de site (G).
 * `compact` é a variante da aba Identidade (status + mesmas ações, sem o resumo).
 */
export function ResearchCard({ ctx, canEdit, reload, compact, focus }: { ctx: Ctx; canEdit: boolean; reload: () => void; compact?: boolean; focus?: boolean }) {
  const run = ctx.latestRun;
  const site = ctx.profile.primaryDomain;
  const { busy, start } = useResearch(reload);
  const [mode, setMode] = useState<'idle' | 'site' | 'rerun'>('idle');
  const [force, setForce] = useState(false);
  const ref = useRef<HTMLElement>(null);
  useEffect(() => { if (focus) ref.current?.scrollIntoView({ behavior: 'smooth', block: 'center' }); }, [focus]);
  const running = active(run);
  const idx = run ? PIPELINE_STAGES.indexOf(run.stage as (typeof PIPELINE_STAGES)[number]) : -1;
  const llm = run?.llm && 'status' in run.llm ? run.llm : null;
  const pending = pendingTotal(ctx);
  const withPending = PENDING_SECTIONS.filter((sec) => (ctx.pendingBySection?.[sec] ?? 0) > 0);
  const firstPending = withPending[0];
  const reviewHref = compact && firstPending ? `/configuracoes/contexto/${SECTION_SLUG[firstPending]}` : '/configuracoes/contexto/visao-geral#revisao';
  const save = (website: string) => start({ website, saveWebsite: true, trigger: site && !sameDomain(site, website) ? 'SITE_CHANGED' : 'FIRST' }).then((ok) => ok && setMode('idle'));
  const rerun = () => start({ force, trigger: 'RERUN' }).then((ok) => { if (ok) { setMode('idle'); setForce(false); } });
  const retry = () => start({ trigger: 'RETRY' });
  const review = () => {
    const el = document.getElementById('revisao');
    if (el && !compact) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    else window.location.assign(reviewHref);
  };

  const sub = 'O InteliMove lê o site e as páginas internas, consulta fontes públicas e a Receita Federal e propõe o contexto. Nada entra sem a sua confirmação.';
  const head = (
    <div className="card-head"><div><h2>Pesquisa da empresa</h2>{!compact && <p className="card-sub">{sub}</p>}</div>
      {run && <span className={`tag ${run.status === 'FAILED' ? 'critical' : run.status === 'PARTIAL' ? 'amber' : running ? 'petrol' : 'mint'}`}>{running ? 'Em andamento' : run.status === 'FAILED' ? 'Falhou' : run.status === 'PARTIAL' ? 'Parcial' : 'Concluída'}</span>}</div>
  );

  // Estado A — sem site: o próprio card pede o site e já inicia a pesquisa.
  if (!site && !running) {
    return (
      <article ref={ref} className={`card research-card${focus ? ' focus-pulse' : ''}`}>
        {head}
        <p>O site da empresa é o ponto de partida: a partir dele o InteliMove encontra páginas internas, dados cadastrais, produtos, mercados e concorrentes.</p>
        {canEdit ? <SiteForm busy={busy} onSubmit={save} autoFocus={focus} /> : <p className="cell-sub">Peça a um administrador para informar o site e iniciar a pesquisa.</p>}
      </article>
    );
  }

  const actions = canEdit && !running && mode === 'idle' && (
    <div className="form-actions">
      {!run && <button className="cta" disabled={busy} onClick={() => start({ trigger: 'FIRST' })}>Pesquisar empresa agora</button>}
      {run && run.status !== 'FAILED' && pending > 0 && <button className="cta" onClick={review}>Revisar sugestões</button>}
      {run?.status === 'PARTIAL' && <button className="secondary" disabled={busy} onClick={retry}>Tentar novamente</button>}
      {run?.status === 'FAILED' && <button className="cta" disabled={busy} onClick={retry}>Tentar novamente</button>}
      {run && run.status !== 'FAILED' && <button className={pending > 0 || run.status === 'PARTIAL' ? 'small-action' : 'cta'} disabled={busy} onClick={() => setMode('rerun')}>Refazer pesquisa</button>}
      <button className="small-action" onClick={() => setMode('site')}>Alterar site</button>
    </div>
  );

  return (
    <article ref={ref} className={`card research-card${focus ? ' focus-pulse' : ''}`}>
      {head}
      <dl className="kv">
        <dt>Site</dt><dd>{site ?? run?.domain}{run && site && !sameDomain(site, run.domain) ? <span className="cell-sub"> · última pesquisa em {run.domain}</span> : null}</dd>
        {run && <><dt>{running ? 'Início' : 'Última pesquisa'}</dt><dd>{clock(running ? run.startedAt ?? run.createdAt : run.finishedAt ?? run.createdAt)}{!running && run.durationMs ? ` · ${Math.round(run.durationMs / 1000)} s` : ''}{run.force ? ' · coleta forçada' : ''}</dd></>}
      </dl>
      {!run && <p className="cell-sub">Nenhuma pesquisa feita ainda para {site}.</p>}

      {/* Estado C — em andamento: progresso real gravado pelo worker. */}
      {running && run && (
        <div aria-live="polite">
          <p><b>{run.status === 'QUEUED' ? 'Aguardando na fila' : STAGE_LABEL[run.stage] ?? run.stage}</b></p>
          {!compact && <ol className="stage-list">{PIPELINE_STAGES.map((s, i) => <li key={s} className={i < idx ? 'done' : i === idx ? 'current' : ''}>{i < idx ? '✓' : i === idx ? '●' : '○'} {STAGE_LABEL[s]}</li>)}</ol>}
          <p className="cell-sub">{run.sourcesDiscovered} fontes descobertas · {run.sourcesCollected} processadas{run.sourcesFailed ? ` · ${run.sourcesFailed} com falha` : ''}</p>
          <p className="cell-sub">Você pode continuar navegando. Esta tela se atualiza sozinha e as sugestões aparecem aqui quando a pesquisa terminar.</p>
          {canEdit && <div className="form-actions"><button className="cta" disabled>Pesquisa em andamento</button></div>}
        </div>
      )}

      {/* Estado F — falha: mensagem de negócio, sem detalhe técnico. */}
      {run?.status === 'FAILED' && (
        <div className="form-error" role="alert">
          Não foi possível concluir a pesquisa da empresa. Nenhuma informação confirmada foi alterada.
          {run.failureReason && <span className="cell-sub"> {run.failureReason}</span>}
          <span className="cell-sub"> Código de suporte: {run.id.slice(0, 8)}</span>
        </div>
      )}

      {/* Estados D/E — concluída ou parcial. */}
      {run && (run.status === 'SUCCEEDED' || run.status === 'PARTIAL') && (
        <div aria-live="polite">
          {run.status === 'PARTIAL' && (
            <div className="notice-inline">
              <b>Pesquisa parcial.</b> O site, as fontes públicas e a Receita foram consultados e os resultados estão disponíveis para revisão.
              {partialReasons(run).length > 0 && <> Não concluído: {partialReasons(run).join('; ')}.</>}
            </div>
          )}
          {!compact && <Summary run={run} />}
          <p className="cell-sub"><b>{pending}</b> sugestões pendentes{withPending.length ? ' em ' : '.'}
            {withPending.map((sec, i) => <span key={sec}>{i ? ', ' : ''}<Link to={`/configuracoes/contexto/${SECTION_SLUG[sec]}`}>{SECTION_TXT[sec]} ({ctx.pendingBySection[sec]})</Link></span>)}{withPending.length ? '.' : ''}</p>
          {!compact && run.identity && 'resolution' in run.identity && <p className="cell-sub"><b>Identidade cadastral:</b> {IDENTITY_TXT[run.identity.resolution]} — {run.identity.reason}</p>}
          {llm && (
            <p className="cell-sub"><b>Síntese por IA:</b> {llm.status === 'CALLED'
              ? `executada (${llm.provider} · ${llm.model ?? '—'} · ${llm.inputTokens + llm.outputTokens} tokens${llm.costUsd !== null ? ` · US$ ${llm.costUsd.toFixed(2)}` : ''}).`
              : llm.status === 'FAILED' ? `não concluída — ${llm.reason}.` : 'não executada: o executor de IA da InteliMove não está configurado neste ambiente. ICPs, personas, objetivos e visão geral por IA dependem dele.'}</p>
          )}
          {!compact && run.notes.length > 0 && <details className="evidence"><summary>Detalhes técnicos da execução ({run.notes.length})</summary><ul className="notes">{run.notes.map((n) => <li key={n}>{n}</li>)}</ul></details>}
        </div>
      )}

      {actions}
      {/* Estado G — troca de site. */}
      {canEdit && mode === 'site' && <SiteForm initial={site} busy={busy} onSubmit={save} onCancel={() => setMode('idle')} autoFocus changing={!!run} locked={ctx.profile.identityLocked} />}
      {canEdit && mode === 'rerun' && (
        <div className="confirm-box" role="dialog" aria-labelledby="rerun-title">
          <p id="rerun-title"><b>Refazer pesquisa?</b></p>
          <p>{RERUN_NOTICE}</p>
          <label className="check"><input type="checkbox" checked={force} onChange={(e) => setForce(e.target.checked)} /> Ler de novo todas as páginas (ignora as lidas nas últimas horas)</label>
          <div className="form-actions"><button className="secondary" onClick={() => setMode('idle')}>Cancelar</button><button className="cta" disabled={busy} onClick={rerun}>Refazer pesquisa</button></div>
        </div>
      )}
    </article>
  );
}

const sameDomain = (a: string, b: string) => a.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '') === b.toLowerCase().replace(/^https?:\/\//, '').replace(/^www\./, '').replace(/\/.*$/, '');

function Summary({ run }: { run: AnalysisRunDto }) {
  const c = run.summary && 'counts' in run.summary ? run.summary.counts : null;
  if (!c) return <p className="card-sub">{run.findingsCreated} sugestões para revisar.</p>;
  const items: Array<[number, string]> = [[c.sources, 'fontes consultadas'], [c.pages ?? 0, 'páginas do site analisadas'], [c.found, 'informações encontradas'], [c.facts, 'fatos'], [c.indications, 'indícios'], [c.hypotheses, 'hipóteses'], [c.conflicts, 'conflitos'], [c.sectionsReady, 'seções com sugestões']];
  return (
    <>
      <ul className="metric-row">{items.map(([n, l]) => <li key={l}><b>{n}</b><span>{l}</span></li>)}</ul>
      {c.reinforced > 0 && <p className="cell-sub">{c.reinforced} informações confirmaram valores que você já tinha.{c.suppressedByRejection ? ` ${c.suppressedByRejection} sugestões rejeitadas antes não voltaram (sem evidência nova).` : ''}</p>}
    </>
  );
}

/** Síntese executiva + cobertura das seções (gerada a partir das seções, com origem declarada). */
export function OverviewSynthesis({ ctx }: { ctx: Ctx }) {
  const run = ctx.latestRun;
  if (!run || active(run) || !('counts' in (run.summary ?? {}))) return null;
  const s = run.summary as Extract<AnalysisRunDto['summary'], { counts: unknown }>;
  const cov = Object.entries(run.coverage ?? {});
  const pct = cov.length ? Math.round((cov.filter(([, v]) => v.state === 'FOUND' || v.state === 'PARTIAL' || v.state === 'CONFLICT').length / cov.length) * 100) : 0;
  const o = s.overview as (typeof s.overview & { generatedBy?: string }) | null;
  return (
    <article className="card" style={{ gridColumn: '1 / -1' }}>
      <div className="card-head"><div><h2>Visão geral da empresa</h2><p className="card-sub">Última pesquisa {run.finishedAt ? relativeTime(run.finishedAt) : '—'} · {pct}% das seções com informação · {o?.generatedBy === 'IA' ? 'síntese por IA a partir das fontes' : 'composta das seções encontradas (sem IA)'}</p></div></div>
      {o?.text && <p className="synthesis">{o.text}</p>}
      {!!o?.lacunas?.length && <p className="cell-sub"><b>Lacunas:</b> {o.lacunas.map((l) => FIELD_LABEL[l] ?? l).join(', ')}</p>}
      <table className="coverage">
        <thead><tr><th>Seção</th><th>Situação</th><th>Não encontrado</th></tr></thead>
        <tbody>{cov.map(([sec, v]) => {
          const [label, tone] = COVERAGE_TXT[v.state] ?? [v.state, 'gray'];
          return <tr key={sec}><td>{SECTION_TXT[sec] ?? sec}</td><td><span className={`tag ${tone}`}>{label}</span></td><td className="cell-sub">{v.notFound.map((f) => FIELD_LABEL[f] ?? f).join(', ') || '—'}{v.note ? ` ${v.note}` : ''}</td></tr>;
        })}</tbody>
      </table>
    </article>
  );
}

const ENTITY_SECTION: Record<string, ContextEntityKind> = { offerings: 'offerings', icps: 'icps', personas: 'personas', competitors: 'competitors' };
const ENTITY_LABEL: Record<ContextEntityKind, string> = { offerings: 'Produto/serviço', icps: 'ICP', personas: 'Persona', competitors: 'Concorrente' };

type Pending = { kind: 'finding'; f: FindingDto; section: string } | { kind: 'entity'; ek: ContextEntityKind; item: Record<string, any>; section: string };

function entityLines(ek: ContextEntityKind, x: Record<string, any>): string[] {
  const d = (x.details ?? {}) as Record<string, any>;
  const L: string[] = [];
  if (x.description) L.push(x.description);
  if (ek === 'offerings') { if (d.category) L.push(`Categoria: ${d.category}`); if (x.problemsSolved?.length) L.push(`Problema resolvido: ${x.problemsSolved.join('; ')}`); if (d.deliverables?.length) L.push(`Entregáveis: ${d.deliverables.join('; ')}`); if (x.targetAudience) L.push(`Público: ${x.targetAudience}`); if (d.sectors?.length) L.push(`Setores: ${d.sectors.join(', ')}`); if (d.commercialModel) L.push(`Modelo comercial: ${d.commercialModel}`); }
  if (ek === 'icps') { if (x.segments?.length) L.push(`Segmentos: ${x.segments.join(', ')}`); if (x.companySizes?.length) L.push(`Porte: ${x.companySizes.join(', ')}`); if (x.locations?.length) L.push(`Localização: ${x.locations.join(', ')}`); if (x.positiveCriteria?.length) L.push(`Critérios: ${x.positiveCriteria.join('; ')}`); if (x.pains?.length) L.push(`Dores: ${x.pains.join('; ')}`); if (d.relatedOfferings?.length) L.push(`Ofertas: ${d.relatedOfferings.join(', ')}`); if (d.toConfirm?.length) L.push(`A confirmar: ${d.toConfirm.join('; ')}`); }
  if (ek === 'personas') { L.push([x.department, x.buyingRole, x.seniority].filter(Boolean).join(' · ')); if (x.pains?.length) L.push(`Dores: ${x.pains.join('; ')}`); if (x.goals?.length) L.push(`Objetivos: ${x.goals.join('; ')}`); if (x.objections?.length) L.push(`Objeções: ${x.objections.join('; ')}`); if (d.kpis?.length) L.push(`Indicadores: ${d.kpis.join('; ')}`); if (d.valueArguments?.length) L.push(`Argumentos de valor: ${d.valueArguments.join('; ')}`); }
  if (ek === 'competitors') { if (x.website) L.push(x.website); if (x.reason) L.push(`Por quê: ${x.reason}`); if (d.similarity) L.push(`Similaridade: ${String(d.similarity).toLowerCase()}`); if (d.overlappingOfferings?.length) L.push(`Ofertas sobrepostas: ${d.overlappingOfferings.join(', ')}`); if (d.overlappingMarkets?.length) L.push(`Mercados sobrepostos: ${d.overlappingMarkets.join(', ')}`); }
  return L.filter(Boolean);
}

/** Revisão das sugestões: por seção, filtros por natureza/confiança, seleção e ações em lote. */
export function ReviewBoard({ ctx, canEdit, reload, section }: { ctx: Ctx; canEdit: boolean; reload: () => void; section?: string }) {
  const { state, reload: again } = useResource((s) => contextAdapter.findings(s).then((r) => r.items), [ctx.profile.revision, ctx.latestRun?.id, ctx.latestRun?.status]);
  const { busy, run } = useAct();
  const [sel, setSel] = useState<Set<string>>(new Set());
  const [nature, setNature] = useState('');
  const [conf, setConf] = useState('');
  const [editing, setEditing] = useState<string | null>(null);
  const done = () => { setSel(new Set()); again(); reload(); };
  const entities = useMemo(() => {
    const out: Pending[] = [];
    for (const [sec, ek] of Object.entries(ENTITY_SECTION)) {
      for (const item of ctx[ek] as unknown as Array<Record<string, any>>) {
        if ((item.status === 'SUGGESTED' || item.status === 'HYPOTHESIS') && item.origin !== 'USER' && !(item as { researched?: boolean }).researched) out.push({ kind: 'entity', ek, item, section: sec });
      }
    }
    return out;
  }, [ctx]);
  const bulkMsg = (r: { accepted: number; rejected: number; skipped: unknown[] }) => `${r.accepted} confirmadas, ${r.rejected} rejeitadas${r.skipped.length ? `, ${r.skipped.length} exigem decisão individual` : ''}.`;
  return (
    <article className="card">
      <div className="card-head"><div><h2>{section ? `Sugestões para ${SECTION_TXT[section]?.toLowerCase()}` : 'Revisão do que foi encontrado'}</h2><p className="card-sub">Cada sugestão mostra natureza, origem, confiança e as evidências. Valor informado por você nunca é trocado sem a sua decisão.</p></div></div>
      <AsyncView state={state} onRetry={again} empty={null}>
        {(findings: FindingDto[]) => {
          const all: Pending[] = [...findings.map((f): Pending => ({ kind: 'finding', f, section: f.section ?? 'identity' })), ...entities]
            .filter((p) => !section || p.section === section)
            .filter((p) => !nature || (p.kind === 'finding' ? p.f.nature : p.item.nature ?? 'HIPOTESE') === nature)
            .filter((p) => !conf || (p.kind === 'finding' ? p.f.confidence : p.item.confidence) === conf);
          const id = (p: Pending) => (p.kind === 'finding' ? p.f.id : p.item.id);
          const groups = new Map<string, Pending[]>();
          for (const p of all) groups.set(p.section, [...(groups.get(p.section) ?? []), p]);
          const selected = all.filter((p) => sel.has(id(p)));
          const payload = (ps: Pending[]) => ({ findingIds: ps.filter((p): p is Extract<Pending, { kind: 'finding' }> => p.kind === 'finding').map((p) => p.f.id), items: ps.filter((p): p is Extract<Pending, { kind: 'entity' }> => p.kind === 'entity').map((p) => ({ kind: p.ek, id: p.item.id })) });
          return (
            <>
              <div className="filter-row">
                <label>Natureza <select value={nature} onChange={(e) => setNature(e.target.value)}><option value="">todas</option>{['FATO', 'INDICIO', 'HIPOTESE', 'CONFLITO'].map((n) => <option key={n} value={n}>{NATURE_TXT[n]}</option>)}</select></label>
                <label>Confiança <select value={conf} onChange={(e) => setConf(e.target.value)}><option value="">todas</option><option value="ALTA">alta</option><option value="MEDIA">média</option><option value="BAIXA">baixa</option></select></label>
                {canEdit && <>
                  <button className="small-action" disabled={busy || !selected.length} onClick={() => run(() => contextAdapter.bulk({ decision: 'ACCEPT', eligibleOnly: false, ...payload(selected) }), bulkMsg, done)}>Confirmar selecionados ({selected.length})</button>
                  <button className="small-action" disabled={busy || !selected.length} onClick={() => run(() => contextAdapter.bulk({ decision: 'REJECT', ...payload(selected) }), bulkMsg, done)}>Rejeitar selecionados</button>
                  {!section && <button className="small-action" disabled={busy || !all.length} title="Só campos vazios, fatos e indícios com confiança média ou alta" onClick={() => run(() => contextAdapter.bulk({ decision: 'ACCEPT', eligibleOnly: true }), bulkMsg, done)}>Confirmar tudo que é elegível</button>}
                </>}
              </div>
              {!all.length && <p className="card-sub">Nenhuma sugestão pendente{section ? ' nesta seção' : ''}.</p>}
              {[...groups.entries()].map(([sec, ps]) => (
                <section key={sec} className="review-section">
                  <div className="card-head"><h3>{SECTION_TXT[sec] ?? sec} <span className="badge">{ps.length}</span></h3>
                    {canEdit && <button className="small-action" disabled={busy} title="Confirma a seção; conflitos e valores protegidos continuam pedindo decisão individual" onClick={() => run(() => contextAdapter.bulk({ decision: 'ACCEPT', section: sec, eligibleOnly: false }), bulkMsg, done)}>Confirmar seção</button>}</div>
                  <ul className="review-list">
                    {ps.map((p) => {
                      const key = id(p);
                      const nat = p.kind === 'finding' ? p.f.nature : (p.item.nature ?? 'HIPOTESE');
                      const confidence = p.kind === 'finding' ? p.f.confidence : p.item.confidence;
                      const origin = p.kind === 'finding' ? p.f.claimOrigin : p.item.claimOrigin;
                      const rationale = p.kind === 'finding' ? p.f.rationale : p.item.rationale;
                      return (
                        <li key={key} className={`suggestion n-${String(nat).toLowerCase()}`}>
                          <div>
                            {canEdit && <input type="checkbox" aria-label="Selecionar" checked={sel.has(key)} onChange={(e) => setSel((cur) => { const n = new Set(cur); if (e.target.checked) n.add(key); else n.delete(key); return n; })} />}{' '}
                            <span className={`tag ${NATURE_TONE[nat] ?? ''}`}>{NATURE_TXT[nat] ?? nat}</span>{' '}
                            {p.kind === 'finding' ? <><b>{FIELD_LABEL[p.f.field] ?? p.f.field}:</b> {show(p.f.value)}</> : <><b>{ENTITY_LABEL[p.ek]}:</b> {p.item.name}</>}
                          </div>
                          <span className="cell-sub">{ORIGIN_TXT[origin ?? ''] ?? 'origem não informada'}{confidence ? ` · confiança ${String(confidence).toLowerCase()}` : ''}{p.kind === 'finding' && p.f.method ? ` · ${METHOD_TXT[p.f.method] ?? p.f.method}` : ''}</span>
                          {p.kind === 'finding' && p.f.mergeState && p.f.mergeState !== 'EMPTY' && <span className="cell-sub merge">{MERGE_TXT[p.f.mergeState]}{p.f.currentValue !== null && p.f.mergeState !== 'CONFLICT' ? ` — atual: ${show(p.f.currentValue)}` : ''}</span>}
                          {p.kind === 'finding' && p.f.alternatives.length > 0 && <span className="cell-sub">{p.f.nature === 'CONFLITO' ? 'Valores encontrados' : 'Outros valores'}: {p.f.alternatives.map((a) => `${show(a.value)} (${a.sources.join(', ')})`).join(' · ')}</span>}
                          {p.kind === 'entity' && entityLines(p.ek, p.item).map((l) => <span key={l} className="cell-sub">{l}</span>)}
                          {rationale && <span className="cell-sub"><i>{rationale}</i></span>}
                          <Evidences items={p.kind === 'finding' ? (p.f.evidences.length ? p.f.evidences : [p.f.evidence]) : (p.item.evidences?.length ? p.item.evidences : [p.item.evidence])} />
                          {canEdit && (p.kind === 'finding' ? (editing === key ? (
                            <form className="inline-form" onSubmit={(e: FormEvent<HTMLFormElement>) => { e.preventDefault(); const v = String(new FormData(e.currentTarget).get('v') ?? ''); run(() => contextAdapter.decide(key, 'EDIT', p.f.field === 'shareCapital' ? Number(v) : v), 'valor editado e confirmado.', () => { setEditing(null); done(); }); }}>
                              <label className="sr-only" htmlFor={`f-${key}`}>Valor corrigido</label>
                              {p.f.alternatives.length > 0 && p.f.nature === 'CONFLITO'
                                ? <select id={`f-${key}`} name="v" defaultValue={show(p.f.value)}>{p.f.alternatives.map((a) => <option key={show(a.value)} value={show(a.value)}>{show(a.value)}</option>)}</select>
                                : <input id={`f-${key}`} name="v" defaultValue={show(p.f.value)} required />}
                              <button className="small-action" disabled={busy}>Aplicar</button><button type="button" className="small-action" onClick={() => setEditing(null)}>Cancelar</button>
                            </form>
                          ) : (
                            <div className="row-actions">
                              {p.f.nature !== 'CONFLITO' && <button className="small-action" disabled={busy} onClick={() => run(() => contextAdapter.decide(key, 'ACCEPT'), 'sugestão confirmada.', done)}>Confirmar</button>}
                              <button className="small-action" disabled={busy} onClick={() => setEditing(key)}>{p.f.nature === 'CONFLITO' ? 'Escolher valor' : 'Editar'}</button>
                              <button className="small-action" disabled={busy} onClick={() => run(() => contextAdapter.decide(key, 'REJECT'), 'sugestão rejeitada.', done)}>Rejeitar</button>
                            </div>
                          )) : (
                            <div className="row-actions">
                              <button className="small-action" disabled={busy} onClick={() => run(() => contextAdapter.act(p.ek, key, 'confirm'), 'item confirmado.', done)}>Confirmar</button>
                              <button className="small-action" disabled={busy} onClick={() => run(() => contextAdapter.act(p.ek, key, 'archive'), 'item rejeitado.', done)}>Rejeitar</button>
                              <span className="cell-sub">Para editar, confirme e ajuste na aba da seção.</span>
                            </div>
                          ))}
                        </li>
                      );
                    })}
                  </ul>
                </section>
              ))}
            </>
          );
        }}
      </AsyncView>
    </article>
  );
}

function SourcesList({ runId }: { runId: string }) {
  const { state, reload } = useResource((s) => contextAdapter.sources(runId, s).then((r) => r.items), [runId]);
  return (
    <AsyncView state={state} onRetry={reload} empty={<p className="card-sub">Nenhuma fonte registrada.</p>}>
      {(items: ContextSourceDto[]) => (
        <table className="coverage">
          <thead><tr><th>#</th><th>Fonte</th><th>Tipo</th><th>Situação</th></tr></thead>
          <tbody>{items.map((s) => (
            <tr key={s.id}>
              <td>{s.key}</td>
              <td>{safeHref(s.finalUrl ?? s.originalUrl) ? <a href={safeHref(s.finalUrl ?? s.originalUrl)} target="_blank" rel="noopener noreferrer">{s.title ?? s.finalUrl ?? s.originalUrl}</a> : s.title}
                {s.redirects.length > 0 && <span className="cell-sub"> · redirecionou de {s.redirects[0]}</span>}</td>
              <td className="cell-sub">{SOURCE_TYPE_TXT[s.type] ?? s.type}{s.role ? ` · ${s.role.toLowerCase()}` : ''}</td>
              <td className="cell-sub">{s.status === 'FAILED' ? `falhou: ${s.failure ?? ''}` : s.status === 'CACHED' ? 'reaproveitada (coleta recente)' : `lida${s.responseMs ? ` em ${s.responseMs} ms` : ''}`}{s.injectionSuspected ? ' · texto suspeito, fora da IA' : ''}</td>
            </tr>
          ))}</tbody>
        </table>
      )}
    </AsyncView>
  );
}

/** Histórico das execuções: parâmetros, fontes, IA, consumo, duração e resultado. */
export function RunHistory() {
  const { state, reload } = useResource((s) => contextAdapter.runs(s).then((r) => r.items), []);
  const [open, setOpen] = useState<string | null>(null);
  return (
    <article className="card">
      <div className="card-head"><div><h2>Pesquisas da empresa</h2><p className="card-sub">Cada execução fica registrada; reexecutar cria um novo conjunto de sugestões sem apagar o anterior.</p></div></div>
      <AsyncView state={state} onRetry={reload} empty={<p className="card-sub">Nenhuma pesquisa ainda.</p>}>
        {(items: AnalysisRunDto[]) => (
          <ul className="review-list">{items.map((r) => {
            const llm = r.llm && 'status' in r.llm ? r.llm : null;
            const c = r.summary && 'counts' in r.summary ? r.summary.counts : null;
            return (
              <li key={r.id}>
                <div><b>{new Date(r.createdAt).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</b> · {r.domain} · {STAGE_LABEL[r.stage] ?? r.stage}{r.force ? ' · coleta forçada' : ''}</div>
                <span className="cell-sub">{r.sourcesCollected} fontes · {r.sourcesFailed} falhas · {r.findingsCreated} sugestões{c ? ` (${c.facts} fatos, ${c.indications} indícios, ${c.hypotheses} hipóteses, ${c.conflicts} conflitos)` : ''}{r.durationMs ? ` · ${Math.round(r.durationMs / 1000)} s` : ''} · {r.analyzer}</span>
                {llm && <span className="cell-sub">IA: {llm.status === 'CALLED' ? `${llm.provider}/${llm.model} · prompt ${llm.promptVersion} (${llm.promptHash}) · ${llm.calls} chamada(s) · ${llm.inputTokens} + ${llm.outputTokens} tokens · ${llm.webSearches} buscas${llm.costUsd !== null ? ` · US$ ${llm.costUsd.toFixed(3)}` : ''}` : `${llm.status.toLowerCase()} — ${llm.reason ?? ''}`}</span>}
                {r.failureReason && <span className="cell-sub">Falha: {r.failureReason}</span>}
                <button className="small-action" onClick={() => setOpen(open === r.id ? null : r.id)}>{open === r.id ? 'Ocultar fontes' : 'Ver fontes consultadas'}</button>
                {open === r.id && <SourcesList runId={r.id} />}
              </li>
            );
          })}</ul>
        )}
      </AsyncView>
    </article>
  );
}

const TRIGGER_TXT: Record<string, string> = { FIRST: 'primeira pesquisa', RERUN: 'reexecução', RETRY: 'nova tentativa', SITE_CHANGED: 'site alterado' };
const ENTITY_TXT: Record<string, string> = { offering: 'Produto/serviço', icp: 'ICP', persona: 'Persona', competitor: 'Concorrente' };
const VERB_TXT: Record<string, string> = { confirmed: 'confirmado', archived: 'arquivado', created: 'cadastrado', updated: 'editado' };

/** Frase de negócio para cada evento da trilha de auditoria do contexto. */
function activityText(a: ContextActivityDto): string {
  const m = a.metadata as Record<string, any>;
  const [, kind, verb] = a.action.split('.');
  switch (a.action) {
    case 'company_context.analysis.requested': return `Pesquisa iniciada em ${m.domain ?? '—'} (${TRIGGER_TXT[m.trigger] ?? 'pesquisa'}${m.force ? ', coleta forçada' : ''})`;
    case 'company_context.analysis.succeeded': return `${m.status === 'PARTIAL' ? 'Pesquisa concluída parcialmente' : 'Pesquisa concluída'}: ${m.sources ?? 0} fontes consultadas, ${m.suggestions ?? 0} sugestões`;
    case 'company_context.analysis.failed': return 'Pesquisa com falha — nenhuma informação confirmada foi alterada';
    case 'company_context.domain.changed': return `Site alterado: ${m.before ?? '(nenhum)'} → ${m.after ?? '—'}`;
    case 'company_context.finding.accepted': return `Sugestão confirmada: ${FIELD_LABEL[m.field] ?? m.field}`;
    case 'company_context.finding.edited': return `Sugestão editada e confirmada: ${FIELD_LABEL[m.field] ?? m.field}`;
    case 'company_context.finding.rejected': return `Sugestão rejeitada: ${FIELD_LABEL[m.field] ?? m.field}`;
    case 'company_context.findings.bulk': return `Decisão em lote: ${m.accepted ?? 0} confirmadas, ${m.rejected ?? 0} rejeitadas${m.skipped ? `, ${m.skipped} deixadas para decisão individual` : ''}`;
    case 'company_context.confirmed': return `Contexto confirmado — versão v${m.version}${m.reason ? ` (“${m.reason}”)` : ''}`;
    case 'company_context.profile.updated': return `Perfil atualizado: ${(m.fields as string[] | undefined)?.map((f) => FIELD_LABEL[f] ?? f).join(', ') ?? '—'}`;
    case 'company_context.identity.changed': return 'Dados cadastrais alterados (com justificativa)';
    case 'company_context.marked_stale': return 'Contexto marcado para revisão';
    default:
      if (kind && ENTITY_TXT[kind] && verb) {
        const v = verb === 'archived' && m.from !== 'CONFIRMED' ? 'rejeitado' : VERB_TXT[verb] ?? verb;
        return `${ENTITY_TXT[kind]} ${v}${m.name ? `: ${m.name}` : ''}${m.origin === 'ENRICHMENT' || m.origin === 'SITE_ANALYSIS' ? ' (sugestão da pesquisa)' : ''}`;
      }
      return a.action;
  }
}

/** IM-EXT-006 — histórico legível: pesquisas, site, decisões e confirmações; detalhes técnicos ficam recolhidos. */
export function ActivityTimeline() {
  const { state, reload } = useResource((s) => contextAdapter.activity(s).then((r) => r.items), []);
  return (
    <article className="card">
      <div className="card-head"><div><h2>Linha do tempo do contexto</h2><p className="card-sub">Pesquisas, alterações do site, sugestões confirmadas, editadas e rejeitadas e versões confirmadas — com quem fez e quando.</p></div></div>
      <AsyncView state={state} onRetry={reload} empty={<p className="card-sub">Nenhum evento ainda.</p>}>
        {(items: ContextActivityDto[]) => (
          <ul className="review-list">{items.map((a) => (
            <li key={a.id}>
              <div><b>{new Date(a.at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })}</b> · {activityText(a)}</div>
              <span className="cell-sub">{a.actor ?? (a.action.startsWith('company_context.analysis.') && a.action !== 'company_context.analysis.requested' ? 'InteliMove (pesquisa automática)' : '—')}</span>
              <details className="evidence"><summary>Detalhes técnicos</summary><pre className="notes">{JSON.stringify({ evento: a.action, alvo: a.targetId, ...a.metadata }, null, 2)}</pre></details>
            </li>
          ))}</ul>
        )}
      </AsyncView>
    </article>
  );
}
